import { useEffect, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiGet, apiPutJson, ApiError } from '../api/client';
import { formatDate, todayIso } from '../lib/format';
import { btn } from '../components/FilterBar';
import { useToast } from '../components/Toast';

/**
 * Kalendar. Ports frmKalendar, frmEvent and UserControlDays.
 *
 * One entry per date, holding that day's text as a single multi-line field —
 * that is the shape of the `calendar` table, verified against production
 * (727 rows, 727 distinct dates).
 */

interface CalendarDay {
  date: string;
  event: string;
}

interface Response {
  year: number;
  month: number;
  monthName: string;
  days: CalendarDay[];
  upcoming: CalendarDay[];
}

const WEEKDAYS = ['Hën', 'Mar', 'Mër', 'Enj', 'Pre', 'Sht', 'Die'];

/** Monday-first offset for the 1st of a month. */
function leadingBlanks(year: number, month: number): number {
  const firstDay = new Date(year, month - 1, 1).getDay(); // 0 = Sunday
  return (firstDay + 6) % 7;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function iso(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export default function Kalendar() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [editing, setEditing] = useState<{ date: string; event: string } | null>(null);
  const [draft, setDraft] = useState('');

  const monthQuery = useQuery({
    queryKey: ['kalendar', year, month],
    queryFn: () => apiGet<Response>('/kalendar', { year, month }),
    placeholderData: (prev) => prev,
  });

  const saveMutation = useMutation({
    mutationFn: ({ date, event }: { date: string; event: string }) =>
      apiPutJson(`/kalendar/${date}`, { event }),
    onSuccess: () => {
      toast.success('Ngjarja u ruajt.');
      void queryClient.invalidateQueries({ queryKey: ['kalendar'] });
      setEditing(null);
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Ruajtja dështoi.'),
  });

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setEditing(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function step(delta: number) {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  }

  const byDate = new Map((monthQuery.data?.days ?? []).map((d) => [d.date.slice(0, 10), d.event]));
  const total = daysInMonth(year, month);
  const blanks = leadingBlanks(year, month);
  const today = todayIso();

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Kalendari</h1>
          <p className="text-sm text-muted">Ngjarjet, ditëlindjet dhe kujtesat e rrogave</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => step(-1)} className={btn.secondary} aria-label="Muaji i kaluar">
            ←
          </button>
          <span className="min-w-40 text-center font-medium">
            {monthQuery.data?.monthName} {year}
          </span>
          <button type="button" onClick={() => step(1)} className={btn.secondary} aria-label="Muaji tjetër">
            →
          </button>
          <button
            type="button"
            onClick={() => {
              const d = new Date();
              setYear(d.getFullYear());
              setMonth(d.getMonth() + 1);
            }}
            className={btn.secondary}
          >
            Sot
          </button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_18rem]">
        <div className="rounded-xl bg-white p-4">
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold uppercase tracking-wide text-muted">
            {WEEKDAYS.map((d) => (
              <div key={d} className="pb-2">
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: blanks }).map((_, i) => (
              <div key={`blank-${i}`} />
            ))}

            {Array.from({ length: total }, (_, i) => i + 1).map((day) => {
              const date = iso(year, month, day);
              const event = byDate.get(date) ?? '';
              const isToday = date === today;

              return (
                <button
                  key={date}
                  type="button"
                  onClick={() => {
                    setDraft(event);
                    setEditing({ date, event });
                  }}
                  className={`min-h-20 rounded-lg border p-1.5 text-left transition-colors ${
                    isToday
                      ? 'border-brand-500 ring-1 ring-brand-200'
                      : 'border-slate-200 hover:border-slate-300'
                  } ${event ? 'bg-brand-50' : 'bg-white'}`}
                >
                  <span
                    className={`text-xs font-semibold ${isToday ? 'text-brand-600' : 'text-muted'}`}
                  >
                    {day}
                  </span>
                  {event && (
                    <span className="mt-0.5 line-clamp-3 block text-[11px] leading-tight text-slate-700">
                      {event}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <aside className="rounded-xl bg-white p-4">
          <h2 className="mb-3 font-semibold">Së shpejti</h2>
          {(monthQuery.data?.upcoming ?? []).length === 0 && (
            <p className="text-sm text-muted">Nuk ka ngjarje të planifikuara.</p>
          )}
          <ul className="space-y-3">
            {(monthQuery.data?.upcoming ?? []).map((u) => (
              <li key={u.date} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                <div className="text-xs font-medium text-brand-600">{formatDate(u.date)}</div>
                <div className="whitespace-pre-line text-sm text-slate-700">{u.event}</div>
              </li>
            ))}
          </ul>
        </aside>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4">
          <div className="w-full max-w-lg rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl">
            <h2 className="mb-1 text-lg font-semibold">Ngjarja e ditës</h2>
            <p className="mb-4 text-sm text-muted">{formatDate(editing.date)}</p>

            <textarea
              rows={8}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Shkruani ngjarjet e kësaj dite…"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            />
            <p className="mt-1 text-xs text-muted">
              Fushë e lirë. Zbrazja e saj e fshin ngjarjen e kësaj dite.
            </p>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Anulo
              </button>
              <button
                type="button"
                disabled={saveMutation.isPending}
                onClick={() => saveMutation.mutate({ date: editing.date, event: draft })}
                className={btn.primary}
              >
                {saveMutation.isPending ? 'Duke ruajtur…' : 'Ruaj'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
